import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import {
  Business,
  Person,
  Email,
  Phone,
  LocationOn,
  AccountBalance,
} from '@mui/icons-material';
import { customerService, INDIAN_STATES, validateGSTIN, CreateCustomerRequest } from '../../services/customerService';

interface QuickAddCustomerProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (customer: any) => void;
}

const initialFormData: CreateCustomerRequest = {
  company_name: '',
  contact_person: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  postal_code: '',
  gstin: '',
  credit_limit: 100000,
  credit_days: 30,
};

const QuickAddCustomer: React.FC<QuickAddCustomerProps> = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CreateCustomerRequest>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gstinError, setGstinError] = useState<string | null>(null);

  const handleChange = (field: keyof CreateCustomerRequest, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Validate GSTIN on change
    if (field === 'gstin') {
      const gstin = (value as string).toUpperCase();
      if (gstin) {
        const validation = validateGSTIN(gstin);
        setGstinError(validation.valid ? null : validation.error || null);

        // Auto-fill state from GSTIN
        if (validation.valid && gstin.length === 15) {
          const stateCode = gstin.substring(0, 2);
          const state = INDIAN_STATES.find(s => s.code === stateCode);
          if (state) {
            setFormData(prev => ({ ...prev, gstin, state: state.name }));
            return;
          }
        }
      } else {
        setGstinError(null);
      }
      setFormData(prev => ({ ...prev, gstin }));
      return;
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.company_name.trim()) {
      setError('Company name is required');
      return;
    }
    if (!formData.contact_person.trim()) {
      setError('Contact person is required');
      return;
    }
    if (formData.gstin && gstinError) {
      setError('Please fix GSTIN errors');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const customer = await customerService.createCustomer(formData);
      onSuccess?.(customer);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setError(null);
    setGstinError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business color="primary" />
          <Typography variant="h6">Add New Customer</Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Basic Info */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              Basic Information
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              label="Company Name"
              value={formData.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Business fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              label="Contact Person"
              value={formData.contact_person}
              onChange={(e) => handleChange('contact_person', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* GST Details */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 1 }}>
              GST Details
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="GSTIN"
              value={formData.gstin}
              onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
              error={!!gstinError}
              helperText={gstinError || 'Format: 22AAAAA0000A1Z5'}
              inputProps={{ maxLength: 15, style: { fontFamily: 'monospace' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountBalance fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              select
              label="State"
              value={formData.state}
              onChange={(e) => handleChange('state', e.target.value)}
            >
              <MenuItem value="">Select State</MenuItem>
              {INDIAN_STATES.map((state) => (
                <MenuItem key={state.code} value={state.name}>
                  {state.code} - {state.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Address */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 1 }}>
              Address
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              multiline
              rows={2}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Postal Code"
              value={formData.postal_code}
              onChange={(e) => handleChange('postal_code', e.target.value)}
              inputProps={{ maxLength: 6 }}
            />
          </Grid>

          {/* Credit Terms */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Credit Days"
              type="number"
              value={formData.credit_days}
              onChange={(e) => handleChange('credit_days', parseInt(e.target.value) || 0)}
              inputProps={{ min: 0, max: 365 }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Credit Limit"
              type="number"
              value={formData.credit_limit}
              onChange={(e) => handleChange('credit_limit', parseInt(e.target.value) || 0)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Creating...' : 'Create Customer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickAddCustomer;
